import { TYPE_BY_VALUE, BRAND } from "./constants";
import JsBarcode from "jsbarcode";

function generateBarcodeDataURL(text, options = {}) {
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(text || "0000"), {
      format: "CODE128",
      displayValue: true,
      fontSize: 14,
      height: 50,
      width: 1.8,
      margin: 6,
      background: "#ffffff",
      lineColor: "#000000",
      ...options,
    });
    return canvas.toDataURL("image/png");
  } catch (e) {
    console.error("Barcode generation failed", e);
    return "";
  }
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("el-GR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateOnly(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("el-GR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

const CHANNEL_LABELS = {
  tel: "ΤΗΛΕΦΩΝΟ",
  mail: "MAIL",
  store: "ΣΤΟ ΚΑΤΑΣΤΗΜΑ",
  client: "ΣΤΟΝ ΠΕΛΑΤΗ",
};

const PRINT_MOUNT_ID = "pinboard-print-root";
const PRINT_STYLE_ID = "pinboard-print-style";

function doPrint(bodyHtml, extraCss = "") {
  // Clean previous
  document.getElementById(PRINT_MOUNT_ID)?.remove();
  document.getElementById(PRINT_STYLE_ID)?.remove();

  const mount = document.createElement("div");
  mount.id = PRINT_MOUNT_ID;
  mount.innerHTML = bodyHtml;
  document.body.appendChild(mount);

  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    @media screen {
      #${PRINT_MOUNT_ID} { display: none !important; }
    }
    @media print {
      html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
      body > *:not(#${PRINT_MOUNT_ID}) { display: none !important; }
      #${PRINT_MOUNT_ID} {
        display: block !important;
        position: static !important;
        margin: 0 !important; padding: 0 !important;
        color: #000 !important; background: #fff !important;
        font-family: Arial, "Helvetica Neue", Helvetica, sans-serif;
      }
      ${extraCss}
    }
  `;
  document.head.appendChild(style);

  const cleanup = () => {
    mount.remove();
    style.remove();
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);
  setTimeout(cleanup, 60000); // safety net

  // Wait for images to load before printing
  const images = mount.querySelectorAll("img");
  const waitForImages = Array.from(images).map((img) => {
    if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
    return new Promise((resolve) => {
      img.addEventListener("load", resolve, { once: true });
      img.addEventListener("error", resolve, { once: true });
      // Safety timeout
      setTimeout(resolve, 4000);
    });
  });

  Promise.all(waitForImages).then(() => {
    try {
      window.focus();
      window.print();
    } catch (e) {
      console.error("Print failed", e);
      cleanup();
    }
  });
}

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_post-it-board-1/artifacts/am5dn8qb_%CE%9A%CE%91%CE%91%CE%A1%CE%A4%CE%91.jpg";

// ─── A4 FULL PRINT ──────────────────────────────────────────────────────────

export function printNote(note) {
  const meta = TYPE_BY_VALUE[note.type] || { label: "" };
  const d = note.data || {};
  const description = escapeHtml(d.description || "").replace(/\n/g, "<br/>");
  const channels = (note.channels || [])
    .map((c) => CHANNEL_LABELS[c] || c)
    .join(" • ");
  const isPickup = note.type === "pickup";
  const isOrder = note.type === "order";
  const hasItems = isOrder && Array.isArray(d.items) && d.items.length > 0;
  const descLabel = isPickup
    ? "ΠΕΡΙΓΡΑΦΗ ΒΛΑΒΗΣ"
    : isOrder
    ? "ΕΙΔΗ"
    : "ΠΕΡΙΓΡΑΦΗ";
  const equipment = isPickup
    ? [d.no_accessories ? "ΧΩΡΙΣ ΠΑΡΕΛΚΟΜΕΝΑ" : null,
       d.power_cable ? "ΚΑΛΩΔΙΟ ΡΕΥΜΑΤΟΣ / ΑΝΤΑΠΤΟΡΑΣ" : null,
       d.connection_cables ? "ΚΑΛΩΔΙΑ ΣΥΝΔΕΣΗΣ" : null,
       d.with_box ? "ΜΕ ΣΥΣΚΕΥΑΣΙΑ / ΚΟΥΤΙ" : null]
        .filter(Boolean)
        .join(" • ")
    : "";

  const barcodeImg = generateBarcodeDataURL(note.seq || note.id, {
    height: 50,
    width: 1.6,
    fontSize: 11,
    margin: 2,
  });

  const body = `
    <div class="a4-sheet">
      <div class="hdr">
        <div class="logo-wrap">
          <img src="${LOGO_URL}" alt="ΚΥΡΙΑΚΙΔΗΣ" class="logo" />
        </div>
        ${barcodeImg ? `<div class="hdr-barcode"><img src="${barcodeImg}" alt="barcode" class="barcode-img" /></div>` : ""}
        <div class="hdr-right">
          <div class="type-pill">${escapeHtml(meta.label)}</div>
        </div>
      </div>

      <div class="customer">${escapeHtml(d.customer || "—")}</div>

      <div class="grid-3">
        <div><div class="label">ΚΩΔΙΚΟΣ / ΑΦΜ</div><div class="value">${escapeHtml(d.code || "—")}</div></div>
        <div><div class="label">ΤΗΛΕΦΩΝΟ</div><div class="value">${escapeHtml(d.phone || "—")}</div></div>
        <div><div class="label">${isPickup ? "ΤΥΠΟΣ" : "ΠΑΡΑΔΟΣΗ"}</div><div class="value">${escapeHtml(
          isPickup
            ? (d.device_type || "—")
            : (d.delivery_date ? formatDateTime(d.delivery_date) : "—")
        )}</div></div>
      </div>

      ${
        isPickup && !d.delivery_date
          ? ""
          : isPickup && d.delivery_date
          ? `<div style="margin-top:10px;"><div class="label">ΠΑΡΑΔΟΣΗ</div><div class="value">${escapeHtml(formatDateTime(d.delivery_date))}</div></div>`
          : ""
      }

      <div style="margin-top:14px;">
        <div class="label">${descLabel}</div>
        ${
          hasItems
            ? `<div class="items-list">${d.items
                .map((it) => {
                  const qty = Math.max(1, parseInt(it.qty ?? 1, 10) || 1);
                  const delivered = Math.max(
                    0,
                    Math.min(
                      qty,
                      parseInt(it.delivered ?? (it.done ? qty : 0), 10) || 0
                    )
                  );
                  const remaining = Math.max(0, qty - delivered);
                  const isDone = delivered >= qty;
                  const isPartial = !isDone && delivered > 0;
                  const status = isDone
                    ? "✓"
                    : isPartial
                    ? `${delivered}/${qty}`
                    : "";
                  const statusLine = isPartial
                    ? `<div class="item-status">ΠΗΓΑΝ ${delivered} · ΥΠΟΛΟΙΠΟ ${remaining}</div>`
                    : "";
                  return `
                  <div class="item-row ${isDone ? "item-done" : ""} ${
                    isPartial ? "item-partial" : ""
                  }">
                    <span class="item-box">${status}</span>
                    <span class="item-qty">${qty}x</span>
                    <div class="item-main">
                      <span class="item-text">${escapeHtml(it.text)}</span>
                      ${statusLine}
                    </div>
                  </div>`;
                })
                .join("")}</div>`
            : `<div class="description">${description || "&nbsp;"}</div>`
        }
      </div>

      ${
        isOrder && (d.notes || "").trim()
          ? `<div style="margin-top:10px;"><div class="label">ΣΗΜΕΙΩΣΕΙΣ</div><div class="description" style="min-height:auto;">${escapeHtml(
              d.notes
            ).replace(/\n/g, "<br/>")}</div></div>`
          : ""
      }

      ${
        isPickup && equipment
          ? `<div style="margin-top:10px;"><div class="label">ΕΞΟΠΛΙΣΜΟΣ ΠΟΥ ΠΑΡΕΛΗΦΘΗ</div><div class="value" style="font-weight:800;">${escapeHtml(equipment)}</div></div>`
          : ""
      }

      <div class="stamp-row">
        <div>
          <div class="label">ΠΗΓΗ ΕΠΙΚΟΙΝΩΝΙΑΣ</div>
          <div class="value">${escapeHtml(channels || "—")}</div>
        </div>
        <div>
          <div class="label">ΧΡΕΩΣΗ</div>
          <div class="charge-box">${escapeHtml(d.charge ? `${d.charge} €` : "—")}</div>
        </div>
        <div class="reg-block">
          <div class="label">ΚΑΤΑΧΩΡΗΣΗ ΑΠΟ</div>
          <div class="value reg-name">${escapeHtml(note.assigned_to || "—")}</div>
          <div class="reg-date">${escapeHtml(formatDateTime(note.note_date || note.created_at))}</div>
        </div>
      </div>

      ${
        note.in_progress
          ? `<div style="margin-top:14px;"><span class="type-pill" style="border-color:#b91c1c;color:#b91c1c;">ΣΕ ΕΞΕΛΙΞΗ ${
              note.in_progress_by ? "— " + escapeHtml(note.in_progress_by) : ""
            }</span></div>`
          : ""
      }

      <div class="footer">${escapeHtml(BRAND)} • POST: ${escapeHtml(note.seq || note.id)}</div>
    </div>
  `;

  const css = `
    @page { size: A4; margin: 14mm; }
    #${PRINT_MOUNT_ID} { font-family: Arial, sans-serif; }
    .a4-sheet { color:#000; }
    .hdr { border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 14px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
    .logo-wrap { flex: 0 0 auto; }
    .logo { display: block; height: 22mm; width: auto; max-width: 70mm; object-fit: contain; }
    .hdr-barcode { flex: 1 1 auto; display: flex; justify-content: center; }
    .barcode-img { max-height: 22mm; width: auto; max-width: 80mm; display: block; }
    .hdr-right { flex: 0 0 auto; text-align: right; }
    .label { font-size: 8pt; letter-spacing: 0.18em; text-transform: uppercase; color: #555; font-weight: 700; }
    .value { font-size: 11pt; font-weight: 600; color: #000; margin-top: 2px; padding-bottom: 4px; border-bottom: 1px solid #ddd; min-height: 18px; }
    .customer { font-size: 22pt; font-weight: 800; margin-bottom: 4px; letter-spacing: 0.02em; }
    .type-pill { display: inline-block; font-size: 9pt; letter-spacing: 0.2em; font-weight: 800; padding: 6px 14px; border: 1.5px solid #000; border-radius: 999px; text-transform: uppercase; }
    .description { margin-top: 6px; font-size: 11pt; line-height: 1.5; white-space: pre-wrap; padding: 10px 12px; border: 1px solid #000; min-height: 90px; }
    .items-list { margin-top: 6px; padding: 8px 12px; border: 1px solid #000; min-height: 60px; }
    .item-row { display: flex; align-items: flex-start; gap: 8px; padding: 4px 0; font-size: 11pt; line-height: 1.4; }
    .item-row + .item-row { border-top: 1px dotted #aaa; }
    .item-box { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 16px; padding: 0 3px; border: 1.5px solid #000; flex-shrink: 0; font-weight: 900; font-size: 8pt; line-height: 1; }
    .item-qty { font-weight: 900; min-width: 22px; flex-shrink: 0; font-size: 10pt; letter-spacing: 0.02em; }
    .item-main { flex: 1; }
    .item-text { display: block; }
    .item-status { font-size: 8pt; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: #b45309; margin-top: 1px; }
    .item-done .item-text { text-decoration: line-through; color: #888; }
    .item-done .item-box { background: #000; color: #fff; }
    .item-partial .item-box { background: #fbbf24; }
    .item-partial { background: #fffbeb; padding-left: 6px; padding-right: 6px; }
    .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-top:14px; }
    .stamp-row { margin-top: 18px; display: grid; grid-template-columns: 1.2fr 1fr 1fr; gap: 14px; align-items: end; }
    .charge-box { border: 2px solid #000; padding: 8px 12px; font-weight: 800; font-size: 14pt; display: inline-block; min-width: 120px; text-align: right; }
    .reg-block { text-align: right; }
    .reg-name { text-align: right; font-weight: 800; }
    .reg-date { font-size: 9pt; color: #444; margin-top: 4px; letter-spacing: 0.1em; }
    .footer { margin-top: 12px; font-size: 8pt; color: #777; text-align: center; letter-spacing: 0.2em; }
  `;
  doPrint(body, css);
}

// ─── 80×100 mm LABEL ─────────────────────────────────────────────────────────

export function printLabel(note) {
  const meta = TYPE_BY_VALUE[note.type] || { label: "" };
  const d = note.data || {};
  const description = escapeHtml(d.description || "").replace(/\n/g, "<br/>");
  const isPickup = note.type === "pickup";

  const lblBarcode = generateBarcodeDataURL(note.seq || note.id, {
    height: 70,
    width: 2.2,
    fontSize: 18,
    margin: 2,
    textMargin: 2,
  });

  const body = `
    <div class="lbl-sheet">
      <div class="lbl-top">
        <img src="${LOGO_URL}" alt="" class="lbl-logo" />
        <div class="lbl-type">${escapeHtml(meta.label)}</div>
      </div>
      <div class="lbl-customer">${escapeHtml(d.customer || "—")}</div>
      <div class="lbl-grid">
        ${d.code ? `<div><b>ΚΩΔ/ΑΦΜ:</b> ${escapeHtml(d.code)}</div>` : ""}
        ${d.phone ? `<div><b>ΤΗΛ:</b> ${escapeHtml(d.phone)}</div>` : ""}
        ${isPickup && d.device_type ? `<div><b>ΤΥΠΟΣ:</b> ${escapeHtml(d.device_type)}</div>` : ""}
        ${d.delivery_date ? `<div><b>ΠΑΡΑΔΟΣΗ:</b> ${escapeHtml(formatDateOnly(d.delivery_date))}</div>` : ""}
        ${isPickup && (d.power_cable || d.connection_cables || d.no_accessories || d.with_box) ? `<div><b>ΕΞΟΠΛ:</b> ${[d.no_accessories ? "ΧΩΡΙΣ ΠΑΡΕΛΚ." : null, d.power_cable ? "ΚΑΛ.ΡΕΥΜΑΤΟΣ" : null, d.connection_cables ? "ΚΑΛ.ΣΥΝΔΕΣΗΣ" : null, d.with_box ? "ΜΕ ΚΟΥΤΙ" : null].filter(Boolean).join(" • ")}</div>` : ""}
      </div>
      ${description ? `<div class="lbl-desc"><b>${isPickup ? "ΒΛΑΒΗ:" : ""}</b> ${description}</div>` : ""}
      <div class="lbl-bottom">
        ${d.charge ? `<div class="lbl-charge">${escapeHtml(d.charge)} €</div>` : "<div></div>"}
        <div class="lbl-reg">
          ${note.assigned_to ? `<div class="lbl-name">${escapeHtml(note.assigned_to)}</div>` : ""}
          <div class="lbl-date">${escapeHtml(formatDateOnly(note.note_date || note.created_at))}</div>
        </div>
      </div>
      ${lblBarcode ? `<div class="lbl-barcode"><img src="${lblBarcode}" alt="barcode" /></div>` : ""}
    </div>
  `;

  const css = `
    @page { size: 80mm 100mm; margin: 0; }
    #${PRINT_MOUNT_ID} { font-family: Arial, sans-serif; width: 80mm; padding: 0; }
    #${PRINT_MOUNT_ID} *, #${PRINT_MOUNT_ID} *::before, #${PRINT_MOUNT_ID} *::after { box-sizing: border-box; }
    .lbl-sheet { color:#000; width: 70mm; height: 90mm; display: flex; flex-direction: column; gap: 1mm; margin: 5mm; padding: 0; overflow: hidden; box-sizing: border-box; }
    .lbl-sheet > * { max-width: 100%; min-width: 0; overflow-wrap: anywhere; word-break: break-word; }
    .lbl-top { display:flex; justify-content: space-between; align-items: center; border-bottom: 1.2px solid #000; padding-bottom: 0.8mm; gap: 1.5mm; flex-shrink: 0; }
    .lbl-logo { height: 7mm; width: auto; max-width: 32mm; object-fit: contain; flex-shrink: 1; }
    .lbl-type { font-weight: 900; letter-spacing: 0.08em; font-size: 7pt; border: 1px solid #000; padding: 0.3mm 1.2mm; border-radius: 3px; white-space: nowrap; flex-shrink: 0; }
    .lbl-customer { font-weight: 900; font-size: 11.5pt; line-height: 1.1; overflow-wrap: anywhere; word-break: break-word; hyphens: auto; flex-shrink: 0; }
    .lbl-grid { font-size: 8pt; line-height: 1.25; display:flex; flex-direction: column; gap: 0.3mm; flex-shrink: 0; }
    .lbl-grid > div { overflow-wrap: anywhere; word-break: break-word; }
    .lbl-grid b { font-weight: 800; letter-spacing: 0.02em; }
    .lbl-desc { font-size: 8.5pt; line-height: 1.2; white-space: pre-wrap; flex: 1 1 auto; padding: 0.8mm; border: 1px dashed #000; overflow: hidden; overflow-wrap: anywhere; word-break: break-word; min-height: 0; }
    .lbl-bottom { display:flex; justify-content: space-between; align-items: flex-end; border-top: 1.2px solid #000; padding-top: 0.8mm; gap: 1.5mm; flex-shrink: 0; }
    .lbl-charge { font-weight: 900; font-size: 11pt; border: 1.5px solid #000; padding: 0.4mm 1.2mm; white-space: nowrap; }
    .lbl-reg { text-align: right; min-width: 0; }
    .lbl-name { font-weight: 800; font-size: 8pt; overflow-wrap: anywhere; }
    .lbl-date { font-size: 7pt; letter-spacing: 0.06em; white-space: nowrap; }
    .lbl-barcode { text-align: center; margin-top: auto; padding-top: 0.5mm; flex-shrink: 0; }
    .lbl-barcode img { max-width: 64mm; max-height: 18mm; }
  `;
  doPrint(body, css);
}
